<script setup>
import FormLabel from "./Label.vue";
import FormErrors from "./Errors.vue";
import _uniqueId from "lodash/uniqueId";
import { computed, ref, useSlots } from "vue";
import { FormHelp, FormHint } from ".";
import { twMerge } from "tailwind-merge";
import { XIcon } from "lucide-vue-next";

defineOptions({
    inheritAttrs: false,
});

const props = defineProps({
    modelValue: {
        type: Array,
        default: [],
    },
    label: {
        type: String,
        default: undefined,
    },
    hint: {
        type: String,
    },
    help: {
        type: String,
    },
    z: {
        type: Number,
        default: 0,
    },
    required: {
        type: Boolean,
        default: false,
    },
    unique: {
        type: Boolean,
        default: true,
    },
    placeholder: {
        type: String,
        required: false,
    },
    additionalSplitters: {
        type: Array,
        default: [],
    },
    errors: {
        type: Array,
        default: [],
    },
    class: String,
});
const emit = defineEmits(["update:modelValue"]);

const slots = useSlots();

const input = ref(null);

const key = _uniqueId("input-");

const wrapperClasses = computed(() => {
    let classes = [
        "w-full rounded-md border-0 p-2 shadow-sm ring-1 ring-inset focus-within:ring-2 focus-within:ring-inset text-sm leading-6",
        "text-gray-900 bg-white ring-gray-300 focus-within:ring-gray-950 placeholder:text-gray-400",
        "flex flex-wrap gap-1",
    ];
    switch (props.z) {
        case 3:
            classes.push("dark:text-white dark:bg-gray-600 dark:ring-gray-500 dark:focus-within:ring-gray-400 dark:placeholder:text-gray-300");
            break;
        case 2:
            classes.push("dark:text-white dark:bg-gray-700 dark:ring-gray-600 dark:focus-within:ring-gray-500 dark:placeholder:text-gray-400");
            break;
        case 1:
            classes.push("dark:text-white dark:bg-gray-800 dark:ring-gray-700 dark:focus-within:ring-gray-600 dark:placeholder:text-gray-400");
            break;
        default:
            classes.push("dark:text-white dark:bg-gray-900 dark:ring-gray-800 dark:focus-within:ring-gray-700 dark:placeholder:text-gray-400");
            break;
    }

    if (props.errors.length > 0) {
        classes.push("ring-red-500 focus-within:ring-red-500 dark:ring-red-500 dark:focus-within:ring-red-500");
    }

    classes.push(props.class);

    return twMerge(classes);
});

const inputClasses = computed(() => {
    let classes = ["flex-1 p-0 bg-transparent border-none appearance-none text-sm leading-6 text-gray-900 placeholder:text-gray-400"];

    switch (props.z) {
        case 3:
            classes.push("dark:text-white dark:placeholder:text-gray-300");
            break;
        case 2:
            classes.push("dark:text-white dark:placeholder:text-gray-400");
            break;
        case 1:
            classes.push("dark:text-white dark:placeholder:text-gray-400");
            break;
        default:
            classes.push("dark:text-white dark:placeholder:text-gray-400");
            break;
    }

    return twMerge(classes);
});

const tagClasses = computed(() => {
    let classes = ["flex items-center gap-2 px-2 py-1 text-xs text-gray-900 bg-gray-100 rounded-md"];

    switch (props.z) {
        case 3:
            classes.push("dark:text-white dark:bg-gray-400");
            break;
        case 2:
            classes.push("dark:text-white dark:bg-gray-500");
            break;
        case 1:
            classes.push("dark:text-white dark:bg-gray-600");
            break;
        default:
            classes.push("dark:text-white dark:bg-gray-700");
            break;
    }

    return twMerge(classes);
});

const removeTag = (tag) => {
    emit(
        "update:modelValue",
        props.modelValue.filter((t) => t !== tag)
    );
};

const keydownHandler = (e) => {
    if (e.key === "Enter" || props.additionalSplitters.includes(e.key)) {
        e.preventDefault();

        let val = input.value.value.trim();
        if (val && (!props.unique || !props.modelValue.includes(val))) {
            emit("update:modelValue", [...props.modelValue, val]);
            input.value.value = "";
        }
    } else if (e.key === "Backspace") {
        if (input.value.value.length === 0 && props.modelValue.length > 0) {
            e.preventDefault();
            input.value.value = props.modelValue[props.modelValue.length - 1];
            emit("update:modelValue", props.modelValue.slice(0, -1));
        }
    }
};

const pasteHandler = (event) => {
    // get pasted text
    let paste = (event.clipboardData || window.clipboardData).getData("text");

    // split pasted text into tags
    let tags = paste.split(new RegExp("[" + ["\n", "\t", ...props.additionalSplitters].join("|") + "]+")).map((tag) => tag.trim());

    // remove empty tags
    tags = tags.filter((tag) => tag.length > 0);

    // remove duplicates
    if (props.unique) {
        tags = tags.filter((tag) => !props.modelValue.includes(tag));
    }

    // add tags to model value
    emit("update:modelValue", [...props.modelValue, ...tags]);
};
</script>

<template>
    <div class="flex flex-col">
        <div :class="[label || !!slots.label ? 'justify-between' : 'justify-end', 'flex items-center space-x-3']">
            <FormLabel v-if="label || !!slots.label" :for="key">
                <slot name="label">{{ label }}</slot>
            </FormLabel>
            <slot name="hint">
                <FormHint v-if="hint" :z="z">
                    {{ hint }}
                </FormHint>
            </slot>
        </div>

        <div :class="[label || !!slots.label || hint || !!slots.hint ? 'mt-2' : '']">
            <div class="relative">
                <div :class="wrapperClasses" @click="$refs.input.focus()">
                    <div v-for="tag in modelValue" :key="tag" :class="tagClasses">
                        {{ tag }}
                        <button type="button" @click="removeTag(tag)">
                            <XIcon class="w-4 h-4" />
                        </button>
                    </div>
                    <input
                        type="text"
                        :class="inputClasses"
                        ref="input"
                        :id="key"
                        @keydown="keydownHandler"
                        @paste.prevent="pasteHandler"
                        :placeholder="placeholder && modelValue.length === 0 ? placeholder : ''" />
                </div>
                <template v-if="!!slots.action">
                    <slot name="action"></slot>
                </template>
            </div>
        </div>

        <slot name="help">
            <FormHelp v-if="help" class="mt-2">
                {{ help }}
            </FormHelp>
        </slot>

        <FormErrors v-if="errors" class="mt-2" :errors="errors" />
    </div>
</template>
